import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  TextField, IconButton, CircularProgress, Stack, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, Chip, Divider
} from "@mui/material";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  AddCircle as AddCircleIcon
} from "@mui/icons-material";

import Swal from "sweetalert2";
import { db } from "../../../firebase";
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, serverTimestamp
} from "firebase/firestore";

const PRIMARY_INDIGO = "#6366F1";

function Posttypes() {
  const [postTypes, setPostTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form State with Subcategories Array
  const [form, setForm] = useState({ title: "", subcategories: [] });
  const [subInput, setSubInput] = useState(""); // Temporary state for the input field

  useEffect(() => {
    fetchPostTypes();
  }, []);

  const fetchPostTypes = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "post_types"), orderBy("createdAt", "asc"));
      const snap = await getDocs(q);
      setPostTypes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Logic to add subcategory to the local array
  const addSubCategory = () => {
    if (!subInput.trim()) return;
    if (form.subcategories.includes(subInput.trim())) {
      Swal.fire("Note", "Subcategory already added", "info");
      return;
    }
    setForm({ ...form, subcategories: [...form.subcategories, subInput.trim()] });
    setSubInput(""); // Clear input
  };

  const removeSubCategory = (indexToRemove) => {
    setForm({
      ...form,
      subcategories: form.subcategories.filter((_, index) => index !== indexToRemove)
    });
  };

  const handleSubmit = async () => {
    if (!form.title) {
      Swal.fire("Error", "Please enter a category title", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: form.title,
        subcategories: form.subcategories, // Array stored directly
        updatedAt: serverTimestamp()
      };

      if (editId) {
        await updateDoc(doc(db, "post_types", editId), payload);
        Swal.fire("Updated", "Category and subcategories updated", "success");
      } else {
        payload.createdAt = serverTimestamp();
        await addDoc(collection(db, "post_types"), payload);
        Swal.fire("Saved", "New Category added", "success");
      }
      resetForm();
      fetchPostTypes();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    const res = await Swal.fire({
      title: "Delete Category?",
      text: "This will remove all subcategories within it.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
    });

    if (res.isConfirmed) {
      await deleteDoc(doc(db, "post_types", id));
      fetchPostTypes();
    }
  };

  const resetForm = () => {
    setForm({ title: "", subcategories: [] });
    setSubInput("");
    setEditId(null);
    setOpen(false);
  };

  const filteredData = postTypes.filter((item) =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box p={1} sx={{ backgroundColor: "#F8FAFC", minHeight: "100vh" }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" mb={3} spacing={2}>
        <Box>
          <Typography variant="h5" fontWeight="900" color="#1E293B">Post Categories</Typography>
          <Typography variant="caption" color="text.secondary">Manage categories and their sub-items</Typography>
        </Box>
        
        <Stack direction="row" spacing={1}>
          <TextField
            placeholder="Search category..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
            sx={{ bgcolor: 'white' }}
          />
          <IconButton onClick={fetchPostTypes}><RefreshIcon fontSize="small" /></IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            sx={{ bgcolor: PRIMARY_INDIGO, textTransform: 'none', fontWeight: 'bold' }}
          >
            Add Category
          </Button>
        </Stack>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}>
        <Table>
          <TableHead sx={{ bgcolor: "#F1F5F9" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Sr.</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Category</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Subcategories</TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5 }}><CircularProgress size={30} /></TableCell></TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5 }}>No categories found</TableCell></TableRow>
            ) : (
              filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{(page * rowsPerPage) + index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#1E293B" }}>{row.title}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {row.subcategories?.length > 0 ? (
                          row.subcategories.map((sub, i) => (
                            <Chip key={i} label={sub} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                          ))
                        ) : (
                          <Typography variant="caption" color="text.disabled">None</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton size="small" 
                          onClick={() => { 
                            setForm({ title: row.title, subcategories: row.subcategories || [] }); 
                            setEditId(row.id); 
                            setOpen(true); 
                          }} 
                          sx={{ color: PRIMARY_INDIGO }}
                        >
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={resetForm} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: "900" }}>{editId ? "Edit" : "New"} Category</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} pt={1}>
            <TextField
              label="Category Name (e.g. Sports)"
              fullWidth
              size="small"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Subcategories</Typography>
              <Stack direction="row" spacing={1} mb={2}>
                <TextField
                  placeholder="Add subcategory..."
                  fullWidth
                  size="small"
                  value={subInput}
                  onChange={(e) => setSubInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addSubCategory()}
                />
                <Button 
                  variant="contained" 
                  onClick={addSubCategory} 
                  sx={{ bgcolor: PRIMARY_INDIGO, minWidth: 'auto' }}
                >
                  <AddCircleIcon />
                </Button>
              </Stack>

              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1, 
                p: 1, 
                border: '1px solid #E2E8F0', 
                borderRadius: 1,
                minHeight: '50px',
                bgcolor: '#F8FAFC'
              }}>
                {form.subcategories.length === 0 && (
                  <Typography variant="caption" color="text.disabled">No subcategories added yet</Typography>
                )}
                {form.subcategories.map((sub, index) => (
                  <Chip
                    key={index}
                    label={sub}
                    onDelete={() => removeSubCategory(index)}
                    color="primary"
                    size="small"
                    sx={{ bgcolor: PRIMARY_INDIGO }}
                  />
                ))}
              </Box>
            </Box>

            <Divider />

            <Button 
              variant="contained" 
              fullWidth 
              onClick={handleSubmit} 
              sx={{ bgcolor: PRIMARY_INDIGO, py: 1.2, fontWeight: 'bold' }}
            >
              {editId ? "Update Category" : "Save Category"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Posttypes;